/**
 * SplitViewCompare — split-screen compare between two FULL-LOD (unified) gaussian splats.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * The original compare implementation clips two splats by screen position on a
 * shared pass. It only works because the "after" splat is loaded NON-unified, so
 * it owns its material and can be clipped independently of the primary. That
 * costs the after splat its streaming LODs: a non-unified splat cannot use the
 * unified octree LOD stream, so compare scenes were limited to a single
 * full-detail blob on the right-hand side.
 *
 * PlayCanvas 2.21 makes a better approach possible. `GSplatDirector` keys its
 * state by (Camera, Layer) — see `gsplat-director.js` — and creates one
 * `GSplatManager` per pair, each owning its own material, work buffer, sorter,
 * and `GSplatWorld` (which does its own octree LOD evaluation and budget
 * balancing). So putting the two splats on two LAYERS gives each one:
 *
 *   - its own material   → independent clip shader, no shared-template clobber
 *   - its own GSplatWorld → independent LOD streaming on BOTH sides
 *   - its own sorter      → correct depth ordering within each side
 *
 * ...while still rendering in a single pass from a single camera. No render
 * targets, no composite post-effect, no per-frame component toggling.
 *
 * ── How the divider stays clean ─────────────────────────────────────────────
 * Clipping in the VERTEX stage alone is not enough. A gaussian whose CENTRE sits
 * just inside the divider still rasterizes its footprint across the line, so the
 * two scenes visibly overlap in a band around the seam (measured at 7px wide on
 * the verification harness). The clip therefore runs in two stages:
 *
 *   1. `gsplatModifyVS` — kills splats whose footprint cannot reach the divider.
 *      This is a bulk cull: it keeps them out of the sort and the raster. The
 *      cull is footprint-aware (it projects each splat's own screen radius), so
 *      it can never punch a hole by discarding a large splat that was still
 *      partly visible.
 *   2. `gsplatModifyPS` — discards the surviving fragments exactly at the
 *      divider, per pixel. This is what makes the line pixel-exact.
 *
 * Verified in a browser (WebGL2, PlayCanvas 2.21.2 and 2.23.2): with both stages, zero
 * pixels of either splat appear on the wrong side of the divider.
 */

import * as pc from 'playcanvas';
import { setSceneSplatBudget } from './splatBudget';

export type SplitOrientation = 'vertical' | 'horizontal';

export interface SplitViewCompareOptions {
    app: pc.Application;
    /** Camera that renders the split. Both split layers are added to it. */
    camera: pc.Entity;
    /** Divider position, 0..1 across the screen. Defaults to 0.5. */
    position?: number;
    /** Divider axis. Defaults to 'vertical'. */
    orientation?: SplitOrientation;
    /**
     * Halve `scene.gsplat.splatBudget` while the split is active. Each layer gets
     * its own GSplatWorld, and each budgets independently against the GLOBAL
     * value — so without this, a split scene streams roughly twice the splats
     * (and twice the work-buffer VRAM) that the budget was sized for.
     * Defaults to true.
     */
    splitSplatBudget?: boolean;
}

export interface SplitViewCompareHandle {
    /** Assign the two splats. Either may be null while it is still loading. */
    setSplats(before: pc.Entity | null, after: pc.Entity | null): void;
    /** Move the divider. Clamped to 0..1. */
    setPosition(position: number): void;
    setOrientation(orientation: SplitOrientation): void;
    /**
     * Update the desired non-split splat budget while compare is active.
     * Positive budgets are divided between the two independent GSplat worlds;
     * unlimited sentinels (zero or negative) use a bounded compare fallback.
     * The latest value is restored exactly on destroy.
     */
    setSplatBudget(totalBudget: number): void;
    /**
     * True once every assigned side has a clipped material. Materials are built
     * lazily on the first frame a (camera, layer) pair renders, so a caller that
     * reveals the scene — the preloader — must wait for this or it will show one
     * unclipped frame with both splats overlapping.
     */
    isReady(): boolean;
    /** The two layers, exposed for diagnostics and for LOD accounting. */
    readonly layers: { before: pc.Layer; after: pc.Layer };
    /** Restores layers, budget, materials, and camera state. Idempotent. */
    destroy(): void;
}

const SIDE_BEFORE = -1;
const SIDE_AFTER = 1;

// An unlimited global budget is unsafe in compare mode because each layer owns
// an independent GSplatWorld. Cap each world at half the balanced desktop
// budget, keeping the combined residency near the existing 5M-splat preset.
const UNLIMITED_COMPARE_SIDE_BUDGET = 2_500_000;

/**
 * Vertex stage: footprint-aware bulk cull.
 *
 * `uCompareExtent` is the camera's right (vertical split) or up (horizontal
 * split) axis in world space, pre-scaled to the gaussian's ~3-sigma reach. We
 * project the centre and a point offset along that axis to get the splat's own
 * screen-space radius, then only cull once the whole footprint is clear of the
 * divider. Culling on the centre alone would erase large splats — sky, distant
 * backdrop — that were still partly visible on our side.
 *
 * For unified splats the work buffer bakes the entity transform into the splat
 * positions, so `modifiedCenter` is already world space and VP alone is correct
 * (no model matrix) — matching the unified branch of the shipped compare clip.
 */
const CLIP_VS_GLSL = /* glsl */ `
uniform float uComparePosition;
uniform float uCompareSide;
uniform float uCompareOrientation;
uniform vec3  uCompareExtent;
uniform mat4  uCompareVP;

void modifySplatCenter(inout vec3 center) {}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    vec4 clipC = uCompareVP * vec4(modifiedCenter, 1.0);
    if (clipC.w <= 0.0) return;

    float maxScale = max(scale.x, max(scale.y, scale.z));
    vec4 clipE = uCompareVP * vec4(modifiedCenter + uCompareExtent * maxScale, 1.0);

    vec2 ndcC = clipC.xy / clipC.w;
    vec2 ndcE = (clipE.w > 0.0) ? (clipE.xy / clipE.w) : ndcC;

    // NDC -> 0..1 screen, Y flipped so 0 = top (same convention as the shipped clip).
    vec2 scrC = vec2(ndcC.x * 0.5 + 0.5, 1.0 - (ndcC.y * 0.5 + 0.5));
    vec2 scrE = vec2(ndcE.x * 0.5 + 0.5, 1.0 - (ndcE.y * 0.5 + 0.5));

    float centerPos = mix(scrC.x, scrC.y, uCompareOrientation);
    float edgePos   = mix(scrE.x, scrE.y, uCompareOrientation);
    float radius    = abs(edgePos - centerPos);

    // Cull only once the ENTIRE footprint is past the divider.
    if ((centerPos - uComparePosition) * uCompareSide < -radius) {
        scale = vec3(0.0);
    }
}

void modifySplatColor(vec3 center, inout vec4 color) {}
`;

/**
 * Fragment stage: the exact edge.
 *
 * `screen_size` is a built-in engine uniform (w, h, 1/w, 1/h) written per camera
 * by the forward renderer (PlayCanvas 2.23 renamed it from `uScreenSize`, which
 * is still set from the same values but deprecated).
 * `uCompareYScale`/`uCompareYOffset` convert the backend's fragment Y into the
 * top-down convention the vertex stage uses: GL's `gl_FragCoord.y` is bottom-up (scale -1, offset 1), WGSL's
 * `@builtin(position).y` is already top-down (scale 1, offset 0).
 *
 * Zeroing alpha rather than `discard` keeps control flow uniform, which the
 * engine's own gsplat fragment shader is careful to preserve so that user
 * chunks may still use derivatives.
 */
const CLIP_PS_GLSL = /* glsl */ `
uniform float uComparePosition;
uniform float uCompareSide;
uniform float uCompareOrientation;
uniform float uCompareYScale;
uniform float uCompareYOffset;
uniform vec4  screen_size;

void modifySplatColor(vec2 gaussianUV, inout vec4 color) {
    vec2 sc = gl_FragCoord.xy * screen_size.zw;
    float screenPos = mix(sc.x, uCompareYScale * sc.y + uCompareYOffset, uCompareOrientation);
    if ((screenPos - uComparePosition) * uCompareSide < 0.0) {
        color.a = 0.0;
    }
}
`;

const CLIP_VS_WGSL = /* wgsl */ `
uniform uComparePosition: f32;
uniform uCompareSide: f32;
uniform uCompareOrientation: f32;
uniform uCompareExtent: vec3f;
uniform uCompareVP: mat4x4f;

fn modifySplatCenter(center: ptr<function, vec3f>) {}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    let clipC = uniform.uCompareVP * vec4f(modifiedCenter, 1.0);
    if (clipC.w <= 0.0) { return; }

    let s = *scale;
    let maxScale = max(s.x, max(s.y, s.z));
    let clipE = uniform.uCompareVP * vec4f(modifiedCenter + uniform.uCompareExtent * maxScale, 1.0);

    let ndcC = clipC.xy / clipC.w;
    var ndcE = ndcC;
    if (clipE.w > 0.0) { ndcE = clipE.xy / clipE.w; }

    let scrC = vec2f(ndcC.x * 0.5 + 0.5, 1.0 - (ndcC.y * 0.5 + 0.5));
    let scrE = vec2f(ndcE.x * 0.5 + 0.5, 1.0 - (ndcE.y * 0.5 + 0.5));

    let centerPos = mix(scrC.x, scrC.y, uniform.uCompareOrientation);
    let edgePos = mix(scrE.x, scrE.y, uniform.uCompareOrientation);
    let radius = abs(edgePos - centerPos);

    if ((centerPos - uniform.uComparePosition) * uniform.uCompareSide < -radius) {
        *scale = vec3f(0.0);
    }
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {}
`;

const CLIP_PS_WGSL = /* wgsl */ `
uniform uComparePosition: f32;
uniform uCompareSide: f32;
uniform uCompareOrientation: f32;
uniform uCompareYScale: f32;
uniform uCompareYOffset: f32;
uniform screen_size: vec4f;

fn modifySplatColor(gaussianUV: vec2f, color: ptr<function, vec4f>) {
    let sc = pcPosition.xy * uniform.screen_size.zw;
    let screenPos = mix(sc.x, uniform.uCompareYScale * sc.y + uniform.uCompareYOffset, uniform.uCompareOrientation);
    if ((screenPos - uniform.uComparePosition) * uniform.uCompareSide < 0.0) {
        (*color).a = 0.0;
    }
}
`;

interface SideState {
    layer: pc.Layer;
    side: number;
    entity: pc.Entity | null;
    /** Layer IDs the entity's gsplat had before we moved it, for restore. */
    previousLayers: number[] | null;
    materials: Map<pc.Material, TrackedMaterial>;
}

type ShaderLanguage = 'glsl' | 'wgsl';
type ShaderChunkKey = 'gsplatModifyVS' | 'gsplatModifyPS';

interface ShaderChunkSnapshot {
    present: boolean;
    value: string | undefined;
}

interface OwnedShaderChunk {
    /** Latest external chunk that compare wrapped. */
    base: ShaderChunkSnapshot;
    /** Exact source installed by compare, used as the ownership token. */
    installed: string;
}

interface TrackedMaterial {
    chunks: Record<ShaderChunkKey, OwnedShaderChunk>;
}

const SHADER_ENTRY_POINTS = [
    'modifySplatCenter',
    'modifySplatRotationScale',
    'modifySplatColor',
] as const;

type ShaderEntryPoint = typeof SHADER_ENTRY_POINTS[number];

function renameEntryPoints(source: string, prefix: string): {
    source: string;
    definitions: Set<ShaderEntryPoint>;
} {
    const definitions = new Set<ShaderEntryPoint>();
    let renamed = source;

    for (const name of SHADER_ENTRY_POINTS) {
        // Shader chunks are required to expose these entry points as either GLSL
        // `void` functions or WGSL `fn`s. Rename every reference only when the
        // source actually defines the function, preserving helper-only snippets.
        const definition = new RegExp(`\\b(?:void|fn)\\s+${name}\\s*\\(`);
        if (!definition.test(source)) continue;
        definitions.add(name);
        renamed = renamed.replace(new RegExp(`\\b${name}\\b`, 'g'), `${prefix}_${name}`);
    }

    return { source: renamed, definitions };
}

function glslVertexWrapper(baseDefinitions: Set<ShaderEntryPoint>): string {
    return `
void modifySplatCenter(inout vec3 center) {
    ${baseDefinitions.has('modifySplatCenter') ? 'splitViewCompareBase_modifySplatCenter(center);' : ''}
    splitViewCompareClip_modifySplatCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    ${baseDefinitions.has('modifySplatRotationScale') ? 'splitViewCompareBase_modifySplatRotationScale(originalCenter, modifiedCenter, rotation, scale);' : ''}
    splitViewCompareClip_modifySplatRotationScale(originalCenter, modifiedCenter, rotation, scale);
}

void modifySplatColor(vec3 center, inout vec4 color) {
    ${baseDefinitions.has('modifySplatColor') ? 'splitViewCompareBase_modifySplatColor(center, color);' : ''}
    splitViewCompareClip_modifySplatColor(center, color);
}
`;
}

function wgslVertexWrapper(baseDefinitions: Set<ShaderEntryPoint>): string {
    return `
fn modifySplatCenter(center: ptr<function, vec3f>) {
    ${baseDefinitions.has('modifySplatCenter') ? 'splitViewCompareBase_modifySplatCenter(center);' : ''}
    splitViewCompareClip_modifySplatCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    ${baseDefinitions.has('modifySplatRotationScale') ? 'splitViewCompareBase_modifySplatRotationScale(originalCenter, modifiedCenter, rotation, scale);' : ''}
    splitViewCompareClip_modifySplatRotationScale(originalCenter, modifiedCenter, rotation, scale);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    ${baseDefinitions.has('modifySplatColor') ? 'splitViewCompareBase_modifySplatColor(center, color);' : ''}
    splitViewCompareClip_modifySplatColor(center, color);
}
`;
}

function glslFragmentWrapper(baseDefinitions: Set<ShaderEntryPoint>): string {
    return `
void modifySplatColor(vec2 gaussianUV, inout vec4 color) {
    ${baseDefinitions.has('modifySplatColor') ? 'splitViewCompareBase_modifySplatColor(gaussianUV, color);' : ''}
    splitViewCompareClip_modifySplatColor(gaussianUV, color);
}
`;
}

function wgslFragmentWrapper(baseDefinitions: Set<ShaderEntryPoint>): string {
    return `
fn modifySplatColor(gaussianUV: vec2f, color: ptr<function, vec4f>) {
    ${baseDefinitions.has('modifySplatColor') ? 'splitViewCompareBase_modifySplatColor(gaussianUV, color);' : ''}
    splitViewCompareClip_modifySplatColor(gaussianUV, color);
}
`;
}

/**
 * Compose compare clipping after an arbitrary existing GSplat shader chunk.
 * Renaming the existing and compare entry points avoids duplicate definitions;
 * a small wrapper then runs the effect first and clipping last. This works for
 * relighting, reveal, audio, mirror, and external chunks without knowing which
 * effect owns the source.
 */
function composeShaderChunk(
    base: ShaderChunkSnapshot,
    clipSource: string,
    lang: ShaderLanguage,
    key: ShaderChunkKey,
): string {
    if (!base.present || !base.value) return clipSource;

    const renamedBase = renameEntryPoints(base.value, 'splitViewCompareBase');
    if (renamedBase.definitions.size === 0) {
        // Helper-only source has no collision with the compare entry points.
        return `${renamedBase.source}\n${clipSource}`;
    }

    const renamedClip = renameEntryPoints(clipSource, 'splitViewCompareClip');
    const wrapper = key === 'gsplatModifyPS'
        ? (lang === 'wgsl'
            ? wgslFragmentWrapper(renamedBase.definitions)
            : glslFragmentWrapper(renamedBase.definitions))
        : (lang === 'wgsl'
            ? wgslVertexWrapper(renamedBase.definitions)
            : glslVertexWrapper(renamedBase.definitions));

    return `${renamedBase.source}\n${renamedClip.source}\n${wrapper}`;
}

export function createSplitViewCompare(options: SplitViewCompareOptions): SplitViewCompareHandle {
    const { app, camera } = options;
    const splitBudget = options.splitSplatBudget !== false;

    let position = clamp01(options.position ?? 0.5);
    let orientation: SplitOrientation = options.orientation ?? 'vertical';
    let destroyed = false;

    const isWebGPU = app.graphicsDevice.isWebGPU;
    const lang: ShaderLanguage = isWebGPU ? 'wgsl' : 'glsl';
    // GL fragment Y is bottom-up; WGSL's is already top-down.
    const yScale = isWebGPU ? 1 : -1;
    const yOffset = isWebGPU ? 0 : 1;

    // --- Layers ---------------------------------------------------------------
    // Inserted immediately after WORLD so splats keep compositing against world
    // geometry the way they do today, and stay ahead of any layer that clears
    // depth (particles, voxel debug) — same placement rule the mirror and
    // hotspot layers use.
    const beforeLayer = new pc.Layer({ name: 'SplitCompareBefore' });
    const afterLayer = new pc.Layer({ name: 'SplitCompareAfter' });
    const worldLayer = app.scene.layers.getLayerById(pc.LAYERID_WORLD);
    const worldIdx = worldLayer ? app.scene.layers.getOpaqueIndex(worldLayer) : -1;
    if (worldIdx >= 0) {
        app.scene.layers.insert(beforeLayer, worldIdx + 1);
        app.scene.layers.insert(afterLayer, worldIdx + 2);
    } else {
        app.scene.layers.push(beforeLayer);
        app.scene.layers.push(afterLayer);
    }

    // Add both layers to the split camera, right after WORLD.
    const cameraComponent = camera.camera;
    const primaryCamera = cameraComponent?.camera ?? null;
    const managedCameras = new Set<pc.CameraComponent>();
    const splitLayerIds = [beforeLayer.id, afterLayer.id];

    const sameLayerOrder = (a: readonly number[], b: readonly number[]): boolean =>
        a.length === b.length && a.every((id, index) => id === b[index]);

    /**
     * Rebuild only our portion of a camera's layer list. Removing the split IDs
     * first prevents duplicates and lets us enforce the primary/secondary rule,
     * while every unrelated layer and every live external ordering change stays
     * intact. Destroy uses the same ownership boundary instead of restoring a
     * stale snapshot of the entire array.
     */
    const setCameraSplitLayers = (component: pc.CameraComponent, ids: number[]): void => {
        const layers = component.layers.filter(id => !splitLayerIds.includes(id));
        if (ids.length > 0) {
            const worldPos = layers.indexOf(pc.LAYERID_WORLD);
            if (worldPos >= 0) layers.splice(worldPos + 1, 0, ...ids);
            else layers.push(...ids);
            managedCameras.add(component);
        }
        if (!sameLayerOrder(component.layers, layers)) component.layers = layers;
    };

    const reconcileCameraLayers = (): void => {
        if (cameraComponent) {
            setCameraSplitLayers(cameraComponent, [beforeLayer.id, afterLayer.id]);
        }

        // Re-scan because mirrors and other cameras can be created after compare
        // starts. A secondary camera gets only BEFORE, and only while it renders
        // WORLD; the primary always retains both split layers.
        app.root.findComponents('camera').forEach((component) => {
            const cam = component as pc.CameraComponent;
            if (cam === cameraComponent) return;
            const rendersWorld = cam.layers.includes(pc.LAYERID_WORLD);
            if (rendersWorld) setCameraSplitLayers(cam, [beforeLayer.id]);
            else if (managedCameras.has(cam)) setCameraSplitLayers(cam, []);
        });
    };

    reconcileCameraLayers();

    // Any OTHER camera that draws WORLD — the mirror reflection cameras — would
    // lose the splats entirely once they move off WORLD onto the split layers.
    // Give those cameras the BEFORE layer only: a reflection shows the unsplit
    // "before" scene, which is coherent, rather than an empty room or a divider
    // computed from a camera it does not belong to. Their materials are separate
    // (materials are per camera AND layer), so they are left unclipped.
    const sides: SideState[] = [
        { layer: beforeLayer, side: SIDE_BEFORE, entity: null, previousLayers: null, materials: new Map() },
        { layer: afterLayer, side: SIDE_AFTER, entity: null, previousLayers: null, materials: new Map() },
    ];

    // --- Splat budget ---------------------------------------------------------
    const sceneGsplat = app.scene.gsplat;
    const originalBudget = sceneGsplat?.splatBudget;
    let desiredBudget = originalBudget;

    const activeBudget = (totalBudget: number): number => {
        if (!splitBudget) return totalBudget;
        return Number.isFinite(totalBudget) && totalBudget > 0
            ? Math.max(1, Math.floor(totalBudget / 2))
            : UNLIMITED_COMPARE_SIDE_BUDGET;
    };

    const applyBudget = (totalBudget: number): void => {
        if (sceneGsplat) setSceneSplatBudget(sceneGsplat, activeBudget(totalBudget));
    };

    if (sceneGsplat && typeof originalBudget === 'number') {
        applyBudget(originalBudget);
    }

    // --- Material wiring ------------------------------------------------------
    const extent = new pc.Vec3();
    const extentArray = [0, 0, 0];
    const vp = new pc.Mat4();

    const snapshotChunk = (
        chunks: Map<string, string>,
        key: ShaderChunkKey,
    ): ShaderChunkSnapshot => ({
        present: chunks.has(key),
        value: chunks.get(key),
    });

    const seedCompareParameters = (material: pc.Material, side: number): void => {
        material.setParameter('uComparePosition', position);
        material.setParameter('uCompareSide', side);
        material.setParameter('uCompareOrientation', orientation === 'horizontal' ? 1 : 0);
        material.setParameter('uCompareYScale', yScale);
        material.setParameter('uCompareYOffset', yOffset);
        material.setParameter('uCompareExtent', [0, 0, 0]);
        material.setParameter('uCompareVP', vp.data);
    };

    const installChunks = (
        material: pc.Material,
        side: number,
        tracked?: TrackedMaterial,
    ): TrackedMaterial => {
        const chunks = material.getShaderChunks(lang);
        const clipSources: Record<ShaderChunkKey, string> = {
            gsplatModifyVS: isWebGPU ? CLIP_VS_WGSL : CLIP_VS_GLSL,
            gsplatModifyPS: isWebGPU ? CLIP_PS_WGSL : CLIP_PS_GLSL,
        };
        const records = tracked?.chunks ?? {} as Record<ShaderChunkKey, OwnedShaderChunk>;
        let changed = false;

        for (const key of Object.keys(clipSources) as ShaderChunkKey[]) {
            const current = snapshotChunk(chunks, key);
            const owned = records[key];
            if (owned && current.present && current.value === owned.installed) continue;

            // A different value means another effect (or a renderer rebuild)
            // changed the chunk. Treat that exact source as the new base and
            // wrap it, so compare never silently steals effect ownership.
            const installed = composeShaderChunk(current, clipSources[key], lang, key);
            chunks.set(key, installed);
            records[key] = { base: current, installed };
            changed = true;
        }

        if (changed) {
            material.update();
            // Seed every uniform AFTER update() rebuilds the shader, so the first
            // rendered frame never reads an unset parameter.
            seedCompareParameters(material, side);
        }

        return tracked ?? { chunks: records };
    };

    const restoreChunks = (material: pc.Material, tracked: TrackedMaterial): void => {
        const chunks = material.getShaderChunks(lang);
        let changed = false;

        for (const key of Object.keys(tracked.chunks) as ShaderChunkKey[]) {
            const owned = tracked.chunks[key];
            // Restore only while our exact installed source is still present.
            // If another effect replaced it, that effect owns teardown.
            if (!chunks.has(key) || chunks.get(key) !== owned.installed) continue;
            if (owned.base.present) chunks.set(key, owned.base.value ?? '');
            else chunks.delete(key);
            changed = true;
        }

        if (changed) material.update();
    };

    const trackMaterial = (material: pc.Material | null | undefined, state: SideState): void => {
        if (!material) return;
        const tracked = installChunks(material, state.side, state.materials.get(material));
        state.materials.set(material, tracked);
    };

    const sideForLayer = (layer: pc.Layer): SideState | undefined =>
        sides.find(s => s.layer === layer);

    // Materials are created lazily, per (camera, layer), on the first frame that
    // pair renders — and again for any additional camera that draws these layers
    // (the mirror reflection camera, for one). Subscribing covers every such
    // pair as it appears; the sweep below covers pairs that already existed.
    // Only the split camera's materials get clipped. A secondary camera rendering
    // the before layer (a mirror) gets its own material for that layer, and
    // clipping it against this camera's VP would produce a nonsense divider.
    const isPrimaryCamera = (cam: pc.Camera | pc.CameraComponent): boolean =>
        primaryCamera == null || cam === primaryCamera || cam === cameraComponent;

    const onMaterialCreated = (
        material: pc.Material,
        cam: pc.Camera | pc.CameraComponent,
        layer: pc.Layer,
    ): void => {
        if (!isPrimaryCamera(cam)) return;
        const state = sideForLayer(layer);
        if (state) trackMaterial(material, state);
    };
    const gsplatSystem = app.systems.gsplat;
    gsplatSystem?.on('material:created', onMaterialCreated);

    /**
     * Reconcile tracked materials against what the engine currently holds for our
     * layers. This both picks up materials built after setup and drops stale ones:
     * switching `scene.gsplat.renderer` at runtime (the editor's LOD settings can)
     * replaces a manager's renderer, and with it the material object. A tracked
     * material that is no longer current would otherwise be written to forever and
     * cleaned up against a discarded object at teardown.
     */
    const reconcileMaterials = (): void => {
        const director = app.renderer.gsplatDirector;
        const current = new Map<SideState, Set<pc.Material>>(sides.map(s => [s, new Set()]));

        director?.camerasMap?.forEach((cameraData, cam) => {
            if (!isPrimaryCamera(cam)) return;
            cameraData.layersMap?.forEach((layerData, layer) => {
                const state = sideForLayer(layer);
                const material = layerData.gsplatManager?.material;
                if (state && material) current.get(state)?.add(material);
            });
        });

        for (const state of sides) {
            const live = current.get(state);
            // A layer with no manager yet reports nothing; that is not staleness,
            // so only prune once the engine actually has a material for this side.
            if (live && live.size > 0) {
                for (const [material, tracked] of [...state.materials]) {
                    if (!live.has(material)) {
                        restoreChunks(material, tracked);
                        state.materials.delete(material);
                    }
                }
            }
            if (live && live.size > 0) {
                live.forEach(material => trackMaterial(material, state));
            } else {
                // An event-created material may not be visible in the director
                // map until the next render. Still detect effect chunk changes.
                for (const material of state.materials.keys()) trackMaterial(material, state);
            }
        }
    };

    // --- Per-frame uniform drive ---------------------------------------------
    const updateUniforms = (): void => {
        if (destroyed) return;
        const cam = camera.camera;
        if (!cam) return;

        vp.mul2(cam.projectionMatrix, cam.viewMatrix);

        // The axis the divider measures along, in world space, scaled to a
        // gaussian's ~3-sigma reach so the vertex cull sees the true footprint.
        const rot = camera.getRotation();
        if (orientation === 'horizontal') rot.transformVector(pc.Vec3.UP, extent);
        else rot.transformVector(pc.Vec3.RIGHT, extent);
        extent.mulScalar(3);

        const orientationValue = orientation === 'horizontal' ? 1 : 0;
        // Reused scratch — this runs every frame.
        extentArray[0] = extent.x;
        extentArray[1] = extent.y;
        extentArray[2] = extent.z;

        for (const state of sides) {
            for (const material of state.materials.keys()) {
                material.setParameter('uComparePosition', position);
                material.setParameter('uCompareSide', state.side);
                material.setParameter('uCompareOrientation', orientationValue);
                material.setParameter('uCompareExtent', extentArray);
                material.setParameter('uCompareVP', vp.data);
            }
        }
    };

    // `framerender` fires after component/script updates and before rendering,
    // which is where per-frame material parameters belong. Also sweep for newly
    // built materials — a splat assigned mid-session gets its manager on the
    // next rendered frame.
    const onFrameRender = (): void => {
        reconcileCameraLayers();
        reconcileMaterials();
        updateUniforms();
    };
    app.on('framerender', onFrameRender);

    // --- Public surface -------------------------------------------------------
    const assignEntity = (state: SideState, entity: pc.Entity | null): void => {
        if (state.entity === entity) return;

        // Put the outgoing entity back where it came from.
        if (state.entity && state.previousLayers) {
            const gsplat = state.entity.gsplat;
            if (gsplat) gsplat.layers = state.previousLayers;
        }
        state.previousLayers = null;

        // Materials belong to the layer, not the entity — but a layer with no
        // splat has no manager, so drop the tracking and let it rebuild.
        for (const [material, tracked] of state.materials) restoreChunks(material, tracked);
        state.materials.clear();

        state.entity = entity;
        if (!entity) return;

        const gsplat = entity.gsplat;
        if (!gsplat) {
            console.warn('[SplitViewCompare] Entity has no gsplat component:', entity.name);
            state.entity = null;
            return;
        }
        state.previousLayers = gsplat.layers ? [...gsplat.layers] : [pc.LAYERID_WORLD];
        gsplat.layers = [state.layer.id];
    };

    return {
        layers: { before: beforeLayer, after: afterLayer },

        setSplats(before, after) {
            if (destroyed) return;
            assignEntity(sides[0], before);
            assignEntity(sides[1], after);
        },

        setPosition(next) {
            if (destroyed) return;
            position = clamp01(next);
        },

        setOrientation(next) {
            if (destroyed) return;
            orientation = next;
        },

        setSplatBudget(totalBudget) {
            if (destroyed || !Number.isFinite(totalBudget)) return;
            desiredBudget = totalBudget;
            applyBudget(totalBudget);
        },

        isReady() {
            if (destroyed) return false;
            // A side with no splat assigned imposes no wait.
            return sides.every(s => !s.entity || s.materials.size > 0);
        },

        destroy() {
            if (destroyed) return;
            destroyed = true;

            app.off('framerender', onFrameRender);
            gsplatSystem?.off('material:created', onMaterialCreated);

            for (const state of sides) {
                for (const [material, tracked] of state.materials) restoreChunks(material, tracked);
                state.materials.clear();
                if (state.entity && state.previousLayers) {
                    const gsplat = state.entity.gsplat;
                    if (gsplat) gsplat.layers = state.previousLayers;
                }
                state.entity = null;
                state.previousLayers = null;
            }

            for (const component of managedCameras) {
                const layers = component.layers.filter(id => !splitLayerIds.includes(id));
                if (!sameLayerOrder(component.layers, layers)) component.layers = layers;
            }
            managedCameras.clear();
            app.scene.layers.remove(beforeLayer);
            app.scene.layers.remove(afterLayer);

            if (sceneGsplat && typeof desiredBudget === 'number') {
                setSceneSplatBudget(sceneGsplat, desiredBudget);
            }
        },
    };
}

function clamp01(value: number): number {
    if (!Number.isFinite(value)) return 0.5;
    return Math.max(0, Math.min(1, value));
}
